import { mpfRListsHttp} from '../api/mpf-r-lists-http'
import { isArchivedFilter } from './data'

interface CommonMetadata {
  AdditionalValue1: string
  AdditionalValue2: string
  AdditionalValue3: string
  AllowEdit: boolean
  Code: null | number
  IsArchived: boolean
  ItemCreated: Date | null | string
  ItemCreatedBy: null | string
  ItemModified: Date | null | string
  ItemModifiedBy: null| string
  MetadataDescription: string
  MetadataType: string
  MetadataValue: string
  OrgComponent: string
  OrgLevel1: string
  OrgLevel2: string
  ParentCode: null |number
  ShortName: string
  eKey: string,
  eTag: string
}

interface CommonMetadataFetchArgs {
  code?:number
  isArchivedFilter: false | null | true
  isAscending?: boolean
  orderBy?: string
  userSections?:[string]
}


const LIST_NAME = 'CommonMetadataList'
const COLUMN_NAMES = {
  AdditionalValue1: 'AdditionalValue1',
  AdditionalValue2: 'AdditionalValue2',
  AdditionalValue3: 'AdditionalValue3',
  AllowEdit: 'AllowEdit',
  Code: 'Code',
  Comments: 'Comments',
  IsArchived: 'IsArchived',
  ItemCreated: 'ItemCreated',
  ItemCreatedBy: 'ItemCreatedBy',
  ItemModified: 'ItemModified',
  ItemModifiedBy: 'ItemModifiedBy',
  MetadataDescription: 'MetadataDescription',
  MetadataType: 'MetadataType',
  MetadataValue: 'MetadataValue',
  OrgComponent: 'OrgComponent',
  OrgLevel1: 'OrgLevel1',
  OrgLevel2: 'OrgLevel2',
  ParentCode: 'ParentCode',
  ShortName: 'ShortName',
  eKey: 'eKey',
  eTag: 'eTag'
}
const COMPONENTS_CODE = {
  Air_Force_Reserve_Command : 144,
  Air_National_Guard : 145,
  Regular_Air_Force: 143,
  Regular_Air_Force_LABEL: 'Regular Air Force'
}
const COMPONENTS_LABEL_CODE = {
  'Air Force Reserve Command' : 144,
  'Air National Guard' : 145,
  'Regular Air Force': 143
}
const SECTION_CODE = {
  'Career Development' : 1,
  'Customer Support' : 2,
  'Force Management': 3,
  'Installation Personnel Readiness': 4,
  'Personnel Systems Management' : 5
}
const getCommonMetadata = async (
  args: CommonMetadataFetchArgs
): Promise<CommonMetadata[]> => {
  const list = await mpfRListsHttp().getList(LIST_NAME)
  const archivedFilter = await isArchivedFilter(args.isArchivedFilter)
  const deletedFilter = `AllowEdit eq true` //filter out 'soft' deleted items
  const filter = archivedFilter ? `(${archivedFilter}) and (${deletedFilter})` : `${deletedFilter}`
  const ascending = args?.isAscending ?? true
  const orderBy = args?.orderBy ?? COLUMN_NAMES.Code

  const items = await list
    .query()
    .filter(filter)
    .select(
      COLUMN_NAMES.AdditionalValue1,
      COLUMN_NAMES.AdditionalValue2,
      COLUMN_NAMES.AdditionalValue3,
      COLUMN_NAMES.AllowEdit,
      COLUMN_NAMES.Code,
      COLUMN_NAMES.IsArchived,
      COLUMN_NAMES.ItemModified,
      COLUMN_NAMES.ItemModifiedBy,
      COLUMN_NAMES.ItemCreated,
      COLUMN_NAMES.ItemCreatedBy,
      COLUMN_NAMES.MetadataType,
      COLUMN_NAMES.MetadataValue,
      COLUMN_NAMES.MetadataDescription,
      COLUMN_NAMES.OrgComponent,
      COLUMN_NAMES.OrgLevel1,
      COLUMN_NAMES.OrgLevel2,
      COLUMN_NAMES.ParentCode,
      COLUMN_NAMES.ShortName,
      COLUMN_NAMES.eKey,
      COLUMN_NAMES.eTag
    )
    .orderBy([{ ascending: ascending, orderBy: orderBy }])
    .get()
    
  return [...items] as CommonMetadata[]
}
const getSectionByCodeOrName = async (
  args: CommonMetadataFetchArgs
): Promise<CommonMetadata[] | null | string> => {
  const list = await mpfRListsHttp().getList(LIST_NAME)
  const archivedFilter = await isArchivedFilter(args.isArchivedFilter)
  const deletedFilter = `AllowEdit eq true` // Filter out 'soft' deleted items
  const filter = archivedFilter
    ? `(${archivedFilter}) and (${deletedFilter}) and (MetadataType eq 'Section')`
    : `${deletedFilter}`
  const ascending = args?.isAscending ?? true
  const orderBy = args?.orderBy ?? COLUMN_NAMES.Code

  const items = await list
    .query()
    .filter(filter)
    .select(
      COLUMN_NAMES.Code,
      COLUMN_NAMES.MetadataType,
      COLUMN_NAMES.MetadataValue
    )
    .orderBy([{ ascending: ascending, orderBy: orderBy }])
    .get()

  // Check for either code or sectionName to return the corresponding MetadataType or Code
  if (args.code) {
    const matchingItem = items.find((item) => item[COLUMN_NAMES.Code] === args.code)
    return matchingItem ? matchingItem[COLUMN_NAMES.MetadataValue] : null
  } else if (args.userSections && Array.isArray(args.userSections)) {
    for (const section of args.userSections) {
      const normalizedSectionName = section.trim().toLowerCase()
      const matchingItem = items.find(
        (item) => item[COLUMN_NAMES.MetadataValue]?.trim().toLowerCase() === normalizedSectionName
      )
      if (matchingItem) {
        return matchingItem[COLUMN_NAMES.Code]
      }
    }
  }

  return [...items] as CommonMetadata[]
}


const addOrUpdateCommonMetadata = async (addObj): Promise<void> => {
  try {
    const andList = await mpfRListsHttp().getList(LIST_NAME) 
    await andList.addOrUpdateItem(addObj)
  } catch (error) {
    console.error(error)
  }
}

interface CommonMetadataItem {
  AllowEdit: boolean;
  eKey: string;
}
const deleteCommonMetadata = async (common): Promise<void> => {
  try {
    const lists = await mpfRListsHttp().getList(LIST_NAME)
    const item = await lists.getItem(common.eKey) as CommonMetadataItem
    item.AllowEdit = false
    // Update the item in the database to be 'soft deleted'
    await lists.addOrUpdateItem(item)
  } catch (error) {
    console.error(`Problem Deleting Common Metadata: ${error}`)
  }
}

const generateIncrementedCommonCode = async (): Promise<number> => {
  const list = await mpfRListsHttp().getList(LIST_NAME)
  const items = await list
  .query()
  .select( COLUMN_NAMES.Code )
  .orderBy([{ ascending: false , orderBy: COLUMN_NAMES.Code }])
  .top(1)
  .get() as CommonMetadata[]

  const existingCode: any = (items).map((m) => m.Code)
  const maxCode = parseInt(existingCode) + 1
  return maxCode as number 
}

const COMPONENTS = {
  AirForceReserveCommand: { abbrValue: 'AFRC', value: 144 },
  AirNationalGuard: { abbrValue: 'ANG', value: 145 },
  RegularAirForce: { abbrValue: 'RegAF', value: 143 },
}

export {
  COLUMN_NAMES,
  COMPONENTS,
  COMPONENTS_CODE,
  COMPONENTS_LABEL_CODE,
  type CommonMetadata,
  type CommonMetadataFetchArgs,
  LIST_NAME,
  SECTION_CODE,
  addOrUpdateCommonMetadata,
  deleteCommonMetadata,
  generateIncrementedCommonCode,
  getCommonMetadata,
  getSectionByCodeOrName,
}
